import React, { ComponentType, lazy, Suspense } from 'react';
import { SvgIconProps } from '@mui/material/SvgIcon';
import { getCoreIcon, isCoreIcon } from './IconBundle';

// Type for the icon map - maps icon names to their lazy components
type LazyIconComponent = ComponentType<SvgIconProps>;

// Icon name mapping with lazy loading
const iconMap: Record<string, () => Promise<{ default: LazyIconComponent }>> = {
  // Core navigation and actions
  AccessTimeIcon: () => import('@mui/icons-material/AccessTime'),
  AddIcon: () => import('@mui/icons-material/Add'),
  AddRoadIcon: () => import('@mui/icons-material/AddRoad'),
  AirplanemodeActiveIcon: () => import('@mui/icons-material/AirplanemodeActive'),
  ArrowBackIcon: () => import('@mui/icons-material/ArrowBack'),
  ArrowDownwardIcon: () => import('@mui/icons-material/ArrowDownward'),
  ArrowDropDownIcon: () => import('@mui/icons-material/ArrowDropDown'),
  ArrowUpwardIcon: () => import('@mui/icons-material/ArrowUpward'),
  BeachAccessIcon: () => import('@mui/icons-material/BeachAccess'),
  BoltIcon: () => import('@mui/icons-material/Bolt'),
  CheckIcon: () => import('@mui/icons-material/Check'),
  ChevronRightIcon: () => import('@mui/icons-material/ChevronRight'),
  CleaningServicesIcon: () => import('@mui/icons-material/CleaningServices'),
  ClearIcon: () => import('@mui/icons-material/Clear'),
  CloseIcon: () => import('@mui/icons-material/Close'),
  CloseFullscreenIcon: () => import('@mui/icons-material/CloseFullscreen'),
  CloudUploadIcon: () => import('@mui/icons-material/CloudUpload'),
  CompassIcon: () => import('@mui/icons-material/Navigation'),
  UploadFileIcon: () => import('@mui/icons-material/UploadFile'),
  ConnectingAirportsIcon: () => import('@mui/icons-material/ConnectingAirports'),
  ContentCopyIcon: () => import('@mui/icons-material/ContentCopy'),
  CopyAllIcon: () => import('@mui/icons-material/CopyAll'),
  DeleteIcon: () => import('@mui/icons-material/Delete'),
  DoneIcon: () => import('@mui/icons-material/Done'),
  DownloadIcon: () => import('@mui/icons-material/Download'),
  EditIcon: () => import('@mui/icons-material/Edit'),
  EditNoteIcon: () => import('@mui/icons-material/EditNote'),
  ExpandLessIcon: () => import('@mui/icons-material/ExpandLess'),
  ExpandMoreIcon: () => import('@mui/icons-material/ExpandMore'),
  FilledFlagIcon: () => import('@mui/icons-material/Flag'),
  FlightIcon: () => import('@mui/icons-material/Flight'),
  FlightLandIcon: () => import('@mui/icons-material/FlightLand'),
  FlightTakeoffIcon: () => import('@mui/icons-material/FlightTakeoff'),
  GpsFixedIcon: () => import('@mui/icons-material/GpsFixed'),
  GridViewIcon: () => import('@mui/icons-material/GridView'),
  GroupIcon: () => import('@mui/icons-material/Group'),
  HelpIcon: () => import('@mui/icons-material/Help'),
  HelpCenterIcon: () => import('@mui/icons-material/HelpCenter'),
  HelpOutlineIcon: () => import('@mui/icons-material/HelpOutline'),
  ImageAspectRatioIcon: () => import('@mui/icons-material/ImageAspectRatio'),
  InfoIcon: () => import('@mui/icons-material/Info'),
  KeyboardDoubleArrowLeftIcon: () => import('@mui/icons-material/KeyboardDoubleArrowLeft'),
  KeyboardDoubleArrowRightIcon: () => import('@mui/icons-material/KeyboardDoubleArrowRight'),
  LabelIcon: () => import('@mui/icons-material/Label'),
  LinearScaleIcon: () => import('@mui/icons-material/LinearScale'),
  LinkIcon: () => import('@mui/icons-material/Link'),
  LocalFireDepartmentIcon: () => import('@mui/icons-material/LocalFireDepartment'),
  LockIcon: () => import('@mui/icons-material/Lock'),
  LockOpenIcon: () => import('@mui/icons-material/LockOpen'),
  LogoutIcon: () => import('@mui/icons-material/Logout'),
  MailOutlineIcon: () => import('@mui/icons-material/MailOutline'),
  MapIcon: () => import('@mui/icons-material/Map'),
  MoreHorizIcon: () => import('@mui/icons-material/MoreHoriz'),
  MyLocationIcon: () => import('@mui/icons-material/MyLocation'),
  NextIcon: () => import('@mui/icons-material/NavigateNext'),
  NearMeIcon: () => import('@mui/icons-material/NearMe'),
  OpenInFullIcon: () => import('@mui/icons-material/OpenInFull'),
  OutlinedFlagIcon: () => import('@mui/icons-material/OutlinedFlag'),
  PictureInPictureIcon: () => import('@mui/icons-material/PictureInPicture'),
  PlayIcon: () => import('@mui/icons-material/PlayArrow'),
  PrintIcon: () => import('@mui/icons-material/Print'),
  RadioButtonUncheckedIcon: () => import('@mui/icons-material/RadioButtonUnchecked'),
  RefreshIcon: () => import('@mui/icons-material/Refresh'),
  RemoveIcon: () => import('@mui/icons-material/Remove'),
  ReplayIcon: () => import('@mui/icons-material/Replay'),
  RocketLaunchIcon: () => import('@mui/icons-material/RocketLaunch'),
  SaveIcon: () => import('@mui/icons-material/Save'),
  ScatterPlotIcon: () => import('@mui/icons-material/ScatterPlot'),
  SearchIcon: () => import('@mui/icons-material/Search'),
  SettingsIcon: () => import('@mui/icons-material/Settings'),
  SpeedIcon: () => import('@mui/icons-material/Speed'),
  SquareFootIcon: () => import('@mui/icons-material/SquareFoot'),
  StopIcon: () => import('@mui/icons-material/Stop'),
  StraightenIcon: () => import('@mui/icons-material/Straighten'),
  SwitchAccountIcon: () => import('@mui/icons-material/SwitchAccount'),
  SwitchLeftIcon: () => import('@mui/icons-material/SwitchLeft'),
  TextFieldsIcon: () => import('@mui/icons-material/TextFields'),
  TextureIcon: () => import('@mui/icons-material/Texture'),
  ThumbUpIcon: () => import('@mui/icons-material/ThumbUp'),
  TitleIcon: () => import('@mui/icons-material/Title'),
  TuneIcon: () => import('@mui/icons-material/Tune'),
  UnfoldLessIcon: () => import('@mui/icons-material/UnfoldLess'),
  WarningIcon: () => import('@mui/icons-material/Warning'),
  WidgetsIcon: () => import('@mui/icons-material/Widgets'),
  CalculateIcon: () => import('@mui/icons-material/Calculate'),
  MonetizationOnIcon: () => import('@mui/icons-material/MonetizationOn'),
  SupportIcon: () => import('@mui/icons-material/Support'),
  SupportAgentIcon: () => import('@mui/icons-material/SupportAgent'),
  // Additional consolidated icons
  CameraAltIcon: () => import('@mui/icons-material/CameraAlt'),
  ChevronLeftIcon: () => import('@mui/icons-material/ChevronLeft'),
  ExpandCircleDownIcon: () => import('@mui/icons-material/ExpandCircleDown'),
  FilterListIcon: () => import('@mui/icons-material/FilterList'),
  GetAppIcon: () => import('@mui/icons-material/GetApp'),
  LaunchIcon: () => import('@mui/icons-material/Launch'),
  MinimizeIcon: () => import('@mui/icons-material/Minimize'),
  NotificationImportantIcon: () => import('@mui/icons-material/NotificationImportant'),
  PauseCircleIcon: () => import('@mui/icons-material/PauseCircle'),
  PhoneIcon: () => import('@mui/icons-material/Phone'),
  PhotoCameraIcon: () => import('@mui/icons-material/PhotoCamera'),
  PlaceIcon: () => import('@mui/icons-material/Place'),
  PublicIcon: () => import('@mui/icons-material/Public'),
  QuestionMarkIcon: () => import('@mui/icons-material/QuestionMark'),
  ScheduleIcon: () => import('@mui/icons-material/Schedule'),
  SmartToyIcon: () => import('@mui/icons-material/SmartToy'),
  SyncAltIcon: () => import('@mui/icons-material/SyncAlt'),
  TimelineIcon: () => import('@mui/icons-material/Timeline'),
  TransformIcon: () => import('@mui/icons-material/Transform'),
  VisibilityIcon: () => import('@mui/icons-material/Visibility'),
  VisibilityOffIcon: () => import('@mui/icons-material/VisibilityOff'),
  ZoomInIcon: () => import('@mui/icons-material/ZoomIn'),
  ZoomOutIcon: () => import('@mui/icons-material/ZoomOut'),
  MoreVertIcon: () => import('@mui/icons-material/MoreVert'),
  StarIcon: () => import('@mui/icons-material/Star'),
  StarBorderIcon: () => import('@mui/icons-material/StarBorder'),
  ViewModuleIcon: () => import('@mui/icons-material/ViewModule'),
  ViewComfyIcon: () => import('@mui/icons-material/ViewComfy'),
  AnalyticsIcon: () => import('@mui/icons-material/Analytics'),
  InsightsIcon: () => import('@mui/icons-material/Insights'),
  PersonIcon: () => import('@mui/icons-material/Person'),
  // Additional missing icons
  BlockIcon: () => import('@mui/icons-material/Block'),
  BugIcon: () => import('@mui/icons-material/BugReport'),
  BuildIcon: () => import('@mui/icons-material/Build'),
  CalendarMonthIcon: () => import('@mui/icons-material/CalendarMonth'),
  NavigateNextIcon: () => import('@mui/icons-material/NavigateNext'),
  ShareIcon: () => import('@mui/icons-material/Share'),
  TableViewIcon: () => import('@mui/icons-material/TableView'),
  NoDataIcon: () => import('@mui/icons-material/EventBusy'),
  Today: () => import('@mui/icons-material/Today'),
};

// Lazy icon component cache for performance
const lazyIconCache = new Map<string, React.LazyExoticComponent<LazyIconComponent>>();

export interface LazyIconProps extends SvgIconProps {
  name: keyof typeof iconMap;
  fallback?: React.ComponentType<SvgIconProps>;
}

// Simple circle fallback icon (inline SVG to avoid imports)
const DefaultFallback = React.forwardRef<SVGSVGElement, SvgIconProps>((props, ref) => (
  <svg 
    ref={ref}
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    {...props}
  >
    <circle cx="12" cy="12" r="8" opacity="0.3" />
  </svg>
));

DefaultFallback.displayName = 'DefaultFallback';

/**
 * Hybrid icon component: uses bundled core icons for common ones,
 * lazy-loads rare icons. Reduces bundle fragmentation while maintaining performance.
 */
export const LazyIcon = React.forwardRef<SVGSVGElement, LazyIconProps>(({ 
  name, 
  fallback: Fallback = DefaultFallback,
  ...props 
}, ref) => {
  // First, check if it's a core bundled icon
  if (isCoreIcon(name)) {
    const CoreIcon = getCoreIcon(name);
    if (CoreIcon) {
      return <CoreIcon ref={ref} {...props} />;
    }
  }

  // Fall back to lazy loading for non-core icons
  if (!lazyIconCache.has(name)) {
    const iconLoader = iconMap[name];
    if (iconLoader) {
      lazyIconCache.set(name, lazy(iconLoader));
    }
  }

  const LazyIconComponent = lazyIconCache.get(name);

  if (!LazyIconComponent) {
    // Fallback for unknown icons
    console.warn(`Icon "${name}" not found in icon map`);
    return <Fallback ref={ref} {...props} />;
  }

  return (
    <Suspense fallback={<Fallback ref={ref} {...props} />}>
      <LazyIconComponent ref={ref} {...props} />
    </Suspense>
  );
});

LazyIcon.displayName = 'LazyIcon';

export default LazyIcon;